import { useQuery, useZero } from "@rocicorp/zero/react";
import { useMemo, useState } from "react";
import { isManagedAiConnectorId } from "../../../shared/aiProviders/managedAiConnector";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import { useHasAccess } from "../../hooks/useHasAccess";
import { usePersonalWorkEnabled } from "../../hooks/usePersonalWorkEnabled";
import { useSpaceDisplayName } from "../../hooks/useSpaceDisplayName";
import { spaceIcon } from "../../lib/spaceIcon";
import {
  FormCard,
  IconContainer,
  RowLabel,
  SectionHeading,
  SpendList,
  SpendRow,
  UsdInput,
} from "../AiProviderModal/modalForm";
import { HelpTooltip } from "../HelpTooltip";
import { Icon, type IconName } from "../Icon";
import { LabelWithInfo } from "../LabelWithInfo";
import { Text } from "../Text";
import { managedAiLifetimeCreditCents } from "../../lib/session";

const TOTAL = "total";
const PERSONAL = "personal";
const inputStyle = { width: 160 } as const;

type ScopeRow = {
  key: string;
  spaceId: string | null;
  scope?: "personal";
  label: string;
  icon: IconName;
};

type Props = {
  connectorId: string;
};

export function UsageLimitsSection({ connectorId }: Props) {
  const z = useZero();
  const canManage = useHasAccess("tenant:ai_providers_manage");
  const personalWorkEnabled = usePersonalWorkEnabled();
  const lifetimeCreditCents = managedAiLifetimeCreditCents();
  const hasLifetimeCredit =
    lifetimeCreditCents != null && isManagedAiConnectorId(connectorId);
  const lifetimeCreditUsd =
    lifetimeCreditCents == null ? null : (lifetimeCreditCents / 100).toFixed(2);
  const [budgets] = useQuery(queries.monitoringUsageBudgets());
  const [spaces] = useQuery(queries.spacesLite({ includeAdminSpaces: true }));
  const displaySpaceName = useSpaceDisplayName();
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const scopeRows = useMemo<ScopeRow[]>(() => {
    const teamSpaces = (spaces ?? [])
      .filter((space) => space.mode !== "personal")
      .map((space) => ({
        key: space.id,
        spaceId: space.id as string | null,
        label: displaySpaceName(space),
        icon: spaceIcon(space.mode),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    const personalRow: ScopeRow[] =
      hasLifetimeCredit || !personalWorkEnabled
        ? []
        : [
            {
              key: PERSONAL,
              spaceId: null,
              scope: "personal" as const,
              label: "Personal",
              icon: spaceIcon("personal"),
            },
          ];
    return [
      {
        key: TOTAL,
        spaceId: null,
        label: hasLifetimeCredit ? "30-day total limit" : "Total spend",
        icon: "wallet" as const,
      },
      ...personalRow,
      ...teamSpaces,
    ];
  }, [spaces, displaySpaceName, hasLifetimeCredit, personalWorkEnabled]);

  const budgetByScope = useMemo(() => {
    const map = new Map<string, { id: string; limitAmount: number }>();
    for (const budget of budgets ?? []) {
      if (budget.connectorId !== connectorId) continue;
      if (budget.measure !== "spend_usd") continue;
      const key =
        budget.scope === "personal" ? PERSONAL : (budget.spaceId ?? TOTAL);
      map.set(key, budget);
    }
    return map;
  }, [budgets, connectorId]);

  const valueFor = (scope: ScopeRow) => {
    const draft = drafts[scope.key];
    if (draft != null) return draft;
    const limit = budgetByScope.get(scope.key)?.limitAmount;
    return limit == null ? "" : String(limit);
  };

  const commit = (row: ScopeRow) => {
    if (!canManage) return;
    const budget = budgetByScope.get(row.key);
    const raw = valueFor(row).trim();
    const amount = raw === "" ? 0 : Number.parseFloat(raw);
    if (!Number.isFinite(amount)) return;
    if (amount <= 0) {
      if (budget) z.mutate(sharedMutators.deleteUsageBudget({ id: budget.id }));
      return;
    }
    if (budget?.limitAmount === amount) return;
    z.mutate(
      sharedMutators.updateUsageBudget({
        connectorId,
        ...(row.scope === "personal"
          ? { scope: "personal" as const }
          : row.spaceId
            ? { spaceId: row.spaceId }
            : {}),
        measure: "spend_usd",
        limitAmount: amount,
      })
    );
  };

  return (
    <FormCard>
      <SectionHeading
        title="Spend limits"
        description={
          hasLifetimeCredit
            ? `Includes $${lifetimeCreditUsd} of Managed by 3B AI usage for the lifetime of the tenant. Connect your own AI API key to continue. Optional 30-day limits can only stop usage sooner. Leave blank for no 30-day limit. Calls already in progress may finish above a limit.`
            : personalWorkEnabled
              ? "Workflow builds and runs, monitor activity, and personal chats using this connector are blocked when the applicable 30-day spend limit is reached. The personal limit applies to each member individually, across their personal chats and personal workflows. Space limits exclude personal usage. Calls already in progress may finish above a limit. Leave blank for no limit."
              : "Workflow builds and runs, and monitor activity using this connector are blocked when the applicable 30-day spend limit is reached. Calls already in progress may finish above a limit. Leave blank for no limit."
        }
      />
      <SpendList>
        {hasLifetimeCredit && (
          <SpendRow>
            <IconContainer>
              <Icon name="wallet" size={16} />
            </IconContainer>
            <RowLabel>
              <Text
                variant="text-regular-md"
                color="var(--text-primary)"
                ellipsis
              >
                Lifetime credit
              </Text>
            </RowLabel>
            <UsdInput
              value={String(lifetimeCreditUsd)}
              disabled
              onChange={() => undefined}
              style={inputStyle}
            />
          </SpendRow>
        )}
        {scopeRows.map((scope) => (
          <SpendRow key={scope.key} onBlur={() => commit(scope)}>
            <IconContainer>
              <Icon name={scope.icon} size={16} />
            </IconContainer>
            <RowLabel>
              {scope.scope === "personal" ? (
                <LabelWithInfo>
                  <Text
                    variant="text-regular-md"
                    color="var(--text-primary)"
                    ellipsis
                  >
                    {scope.label}
                  </Text>
                  <HelpTooltip content="Applies to each member’s personal chats and personal workflows." />
                </LabelWithInfo>
              ) : (
                <Text
                  variant="text-regular-md"
                  color="var(--text-primary)"
                  ellipsis
                >
                  {scope.label}
                </Text>
              )}
            </RowLabel>
            <UsdInput
              value={valueFor(scope)}
              placeholder="0.00"
              disabled={!canManage}
              onChange={(next) =>
                setDrafts((prev) => ({ ...prev, [scope.key]: next }))
              }
              style={inputStyle}
            />
          </SpendRow>
        ))}
      </SpendList>
    </FormCard>
  );
}
